import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@attendance/ui/utils';
import { focusRing } from '@attendance/ui/focus';
import { CalendarDays, CircleUser, Clock, Home, Menu } from 'lucide-react';
import { useFeatureEnabled } from '../../stores/brandingStore.js';
import { useAuthStore } from '../../stores/authStore.js';

/**
 * MOBILE NAVIGATION AS GLASS OVER THE PAGE — no slab, no box.
 *
 * It has been three things. A full-bleed brand-coloured bar welded to the
 * bottom edge; then the desktop rail turned on its side, an INK capsule
 * floating above the page. Both were a box: a solid shape that occupies the
 * bottom of every screen whether or not anything is under it, and reads as a
 * second surface the page has to end above (owner: "why you keep background box
 * behind nav menu — we can keep nav above content, only blurred shadow to make
 * it pop out of the content").
 *
 * So the ground is the page's own, at 85%, with a blur behind it and a soft
 * shadow under it — `bg-surface/85 shadow-popover backdrop-blur-md`, which is
 * the app's existing floating-surface idiom (`authCardSurfaceClass`) rather
 * than a shape invented here. What is under the bar stays legible THROUGH it,
 * slightly out of focus, which is what tells a reader the page continues rather
 * than stops. The shadow is the only thing separating the two, and separation
 * is all it is asked to do.
 *
 * WHY NOT NO SURFACE AT ALL: glyphs laid directly on a scrolling page sit on
 * whatever passes under them — a photo, a table, a dark chart — and a control
 * whose legibility depends on what the reader scrolled to is not a control. The
 * blur is what buys the tint down to 85%.
 *
 * AND THE SELECTION INVERTS WITH THE GROUND. It was a white pill on ink; on a
 * pale bar white is the bar. It is the brand fill now — `--primary` with its
 * paired foreground, the one point of colour on the bar, and the same language
 * the rail uses to mark the row you are standing on.
 *
 * ── THE ACTIVE TAB IS A PILL INSIDE THE CAPSULE ────────────────────────────
 *
 * A white pill carrying the icon AND the name; everything else is a glyph.
 * Labelling only WHERE YOU ARE is the settled answer to a question this bar has
 * been through twice: full captions cost a third of the bar's height to label
 * four things a worker taps daily, and no captions at all leave a bilingual
 * workforce guessing at glyphs. The label is one word, and it is the word a
 * reader most wants — the others are reachable by tapping, but "which of these
 * am I on" is not.
 *
 * It used to RISE OUT of the capsule on a negative margin (owner: "white
 * highlight should be inside the nav menu"). A mark that breaks its container's
 * edge reads as a separate floating object rather than as the state of a row,
 * and against a page it overlapped it had no ground of its own to sit on. It is
 * a pill in the row now, laid out along it, so the capsule contains its own
 * selection.
 *
 * ── AND THE TRAILING DISC IS THE MENU ──────────────────────────────────────
 *
 * It was the punch: a purple disc that PERFORMED a check-in on the home screen
 * and navigated to the home screen everywhere else. Both halves were the same
 * screen — home is where a worker checks in, and the panel there carries the
 * button with the reason it is or is not available on it — so the disc was a
 * second door to a control that was already one tap away, in the shape the app
 * uses for its single most consequential action (owner: "check in is same as
 * home").
 *
 * It opens the NAVIGATION DRAWER instead, which is the one thing this bar could
 * not reach: the four tabs are the four screens a worker uses daily, and
 * Corrections, Announcements and Letters live behind the drawer. Its trigger
 * was a hamburger in the top-left of the header — the far corner from a thumb,
 * on the surface a worker holds one-handed. Same drawer, same single trigger,
 * moved to where the hand already is.
 *
 * ── WHO GETS IT ────────────────────────────────────────────────────────────
 *
 * Every slot needs an Employee record behind the login, so a management-only
 * console renders NOTHING here — and `AppShell` does not mount it outside the
 * worker's workspace at all (`shellSurface.ts`). Hidden at md+, where the rail
 * itself is on screen.
 */

/** THE APP'S FOCUS RING, now that the bar is on the app's own ground. It used
 *  to be spelt out in rail ink, because `--ring` is `--primary` and a brand ring
 *  on an ink capsule is nearly invisible. On a pale surface that reason is gone
 *  and the canon ring is both correct and one import. */
const CAPSULE_FOCUS = focusRing;

/** The floating bar's ground: the page's own surface at 85%, a blur behind it,
 *  a soft shadow under it. Shared by the capsule and the menu disc so the two
 *  cannot drift into being two materials. */
const GLASS = 'bg-surface/85 shadow-popover backdrop-blur-md';

export function MobileBottomNav({ onOpenMenu }: { onOpenMenu: () => void }): JSX.Element | null {
  const { t } = useTranslation();
  const leaveOn = useFeatureEnabled('leave');
  const hasEmployeeRecord = useAuthStore((s) => Boolean(s.user?.employeeId));
  const selfService = useFeatureEnabled('selfService') && hasEmployeeRecord;
  if (!selfService) return null;

  const tabs = [
    { to: '/', end: true, icon: Home, label: t('nav.home', 'Home') },
    { to: '/my-attendance', icon: Clock, label: t('mobileNav.attendance', 'Attendance') },
    ...(leaveOn
      ? [{ to: '/leave', icon: CalendarDays, label: t('mobileNav.leave', 'Leave') }]
      : []),
    { to: '/profile', icon: CircleUser, label: t('mobileNav.profile', 'Profile') },
  ];

  const menuLabel = t('appShell.openMenu', 'Open menu');

  return (
    /* `items-center` — the capsule and the menu disc are two objects of the
       same height sitting on one line. It was `items-end`, which existed only
       to give the risen active tab a floor to rise from. */
    /* `bottom-4` FLOATS IT; the safe-area padding sits UNDER that.
       
       Both used to be padding-bottom on this element — `pb-4 pb-[env(...)]` —
       and the second wins, so on every browser that resolves the inset to 0
       (which is most of them) the capsule was welded to the bottom edge with
       no gap at all. Two declarations of one property is not two rules. The
       offset is a position now and the inset is the only padding. */
    <div className="fixed inset-x-0 bottom-4 z-30 flex items-center justify-center gap-element px-gutter-sm pb-[env(safe-area-inset-bottom)] md:hidden">
      <nav
        aria-label={t('mobileNav.label', 'Primary')}
        className={cn('flex items-center gap-tight rounded-full p-1.5', GLASS)}
      >
        {tabs.map(({ to, end, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            {...(end ? { end: true } : {})}
            aria-label={label}
            className={({ isActive }) =>
              cn(
                'flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full transition-colors duration-fast',
                CAPSULE_FOCUS,
                isActive
                  ? /* A PILL ALONG THE ROW, not a disc above it. The label sits
                       BESIDE the glyph rather than under it, which is what lets
                       the selected slot say its name without making the capsule
                       taller than the row it labels — and `px-3` lets it grow to
                       its word, so "Attendance" is never cut to "Atten…". Short
                       words still come out round on the `min-w-touch` floor. */
                    'gap-tight bg-primary px-3 text-primary-foreground shadow-hover'
                  : 'text-foreground-tertiary hover:bg-muted hover:text-foreground'
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={isActive ? 20 : 22} aria-hidden />
                {isActive && (
                  /* `text-label` is the 12.5px/600 label step — the token for
                     naming a control, which is what this is. It is the ONE
                     caption on the bar. */
                  <span className="whitespace-nowrap text-label leading-none">
                    {label}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* THE DRAWER'S ONE TRIGGER. A button rather than a link because it
          opens a surface on this screen; `aria-controls`/`aria-expanded` are
          the shell's, and they stay with the drawer it owns — this passes the
          request up rather than holding a second copy of that state. */}
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={menuLabel}
        aria-controls="mobile-nav"
        className={cn(
          'flex h-14 w-14 min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full text-foreground-tertiary transition-colors duration-fast hover:text-foreground',
          GLASS,
          CAPSULE_FOCUS
        )}
      >
        <Menu size={22} aria-hidden />
      </button>
    </div>
  );
}
